import { type ClientCatalogBehavior, toClientCatalogBehavior } from "@core/FileStructue/Catalog/CatalogBehavior";
import { ItemType } from "@core/FileStructue/Item/ItemType";
import type { CategoryLink, ItemLink } from "@ext/navigation/NavigationLinks";
import { createContext, createElement, type ReactNode, useContext, useRef } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { createStore, type StateCreator } from "zustand/vanilla";
import { DropMode, type PersistedDropMode } from "../utils/dropMode";
import { getTreeId } from "../utils/getTreeId";

export type DragTarget = {
	anchorId: string;
	parentId: string | null;
	mode: DropMode;
} | null;

const EMPTY_DRAG_LINE_IDS: ReadonlySet<string> = new Set();
const EMPTY_FILTERED_OUT_IDS: ReadonlySet<string> = new Set();

const NO_CATALOG_BEHAVIOR = toClientCatalogBehavior({});

export const isReadOnlyGroup = (state: NavigationTreeStore, id: string): boolean =>
	state.catalogBehavior.readOnlyTopCategories &&
	state.rootIds.includes(id) &&
	state.flatIndex[id]?.type === ItemType.category;

export const hasItemMenu = (state: NavigationTreeStore, id: string): boolean =>
	!(state.catalogBehavior.limitedActions && isReadOnlyGroup(state, id));

const landsAtRoot = (target: DragTarget): boolean =>
	target.mode === DropMode.LastRoot ||
	((target.mode === DropMode.Before || target.mode === DropMode.After) && target.parentId === null);

export const isUnnestableArticle = (state: NavigationTreeStore, id: string): boolean =>
	state.catalogBehavior.readOnlyTopCategories &&
	state.rootIds.includes(id) &&
	state.flatIndex[id]?.type === ItemType.article;

const isAllowedDrop = (state: NavigationTreeStore, target: DragTarget): boolean => {
	const { noRootArticles, readOnlyTopCategories } = state.catalogBehavior;
	const arrivesAtRoot = landsAtRoot(target) && !state.rootIds.includes(state.draggingId);
	const isCategory = state.flatIndex[state.draggingId]?.type === ItemType.category;
	if (arrivesAtRoot && (noRootArticles || (readOnlyTopCategories && isCategory))) return false;
	return !(target.mode === DropMode.Into && isUnnestableArticle(state, target.anchorId));
};

const sameDragTarget = (left: DragTarget, right: DragTarget): boolean =>
	left === right ||
	(!!left &&
		!!right &&
		left.anchorId === right.anchorId &&
		left.parentId === right.parentId &&
		left.mode === right.mode);

const getDragLineIds = (target: DragTarget, childrenMap: ChildrenMap): ReadonlySet<string> => {
	if (target?.mode !== DropMode.After || !target.parentId) return EMPTY_DRAG_LINE_IDS;
	const siblings = childrenMap[target.parentId];
	if (!siblings) return EMPTY_DRAG_LINE_IDS;
	const anchorIndex = siblings.indexOf(target.anchorId);
	return anchorIndex < 0 ? EMPTY_DRAG_LINE_IDS : new Set(siblings.slice(0, anchorIndex + 1));
};

// Flat index built from the nested ItemLink tree
export type FlatIndex = Record<string, ItemLink>;
export type ChildrenMap = Record<string, string[]>; // parentPath → childPaths[]
export type ParentMap = Record<string, string>; // childPath → parentPath

export const NAVIGATION_HOVER_ID_ATTR = "data-navigation-hover-id";
export const NAVIGATION_ROW_SELECTOR = `[${NAVIGATION_HOVER_ID_ATTR}]`;

export type NavigationTreeStore = {
	scope: string | null;
	flatIndex: FlatIndex;
	childrenMap: ChildrenMap;
	parentMap: ParentMap;
	rootIds: string[];
	expanded: ReadonlySet<string>;
	selectedId: string;
	hoveredParentId: string | null;
	hoveredAnchorId: string | null;
	hoveredNavigationId: string | null;
	draggingId: string | null;
	dragTarget: DragTarget;
	dragLineIds: ReadonlySet<string>;
	isDragLocked: boolean;
	filteredOutIds: ReadonlySet<string>;
	isGrouped: boolean;
	catalogBehavior: ClientCatalogBehavior;
	/** Bumped when a collapse/expand animation settles, so measured rects can be refreshed. */
	layoutVersion: number;
	onDrop: ((draggedId: string, anchorId: string, mode: PersistedDropMode) => Promise<void>) | null;
	onToggle: ((id: string, open: boolean) => void) | null;
	onCreateArticle: ((parentId?: string, afterId?: string) => Promise<void>) | null;
	setDragLocked: (locked: boolean) => void;
	setCatalogBehavior: (behavior: ClientCatalogBehavior) => void;
	notifyLayoutSettled: () => void;
	toggleExpanded: (id: string, open: boolean) => void;
	select: (id: string) => void;
	setHover: (parentId: string | null, anchorId: string | null) => void;
	setHoveredNavigationId: (id: string | null) => void;
	setDragging: (id: string | null) => void;
	setDragTarget: (target: DragTarget) => void;
	setNavItems: (links: ItemLink[], scope?: string) => void;
	moveToGroup: (itemId: string, groupId: string) => void;
	snapshotTree: () => NavigationTreeSnapshot;
	restoreTree: (snapshot: NavigationTreeSnapshot) => void;
	setGrouped: (isGrouped: boolean) => void;
	markFilteredOut: (itemId: string, filteredOut: boolean) => void;
	setOnDrop: (fn: NavigationTreeStore["onDrop"]) => void;
	setOnToggle: (fn: NavigationTreeStore["onToggle"]) => void;
	setOnCreateArticle: (fn: NavigationTreeStore["onCreateArticle"]) => void;
};

export type NavigationTreeSnapshot = Pick<
	NavigationTreeStore,
	"flatIndex" | "childrenMap" | "parentMap" | "rootIds" | "expanded"
>;

const sameItem = (a: ItemLink | undefined, b: ItemLink): boolean =>
	!!a &&
	a.type === b.type &&
	a.title === b.title &&
	a.icon === b.icon &&
	a.isCurrentLink === b.isCurrentLink &&
	a.pathname === b.pathname &&
	a.external === b.external &&
	a.status === b.status &&
	(a as CategoryLink).isExpanded === (b as CategoryLink).isExpanded &&
	(a as CategoryLink).existContent === (b as CategoryLink).existContent;

const sameIds = (a: string[] | undefined, b: string[]): boolean =>
	!!a && a.length === b.length && a.every((id, i) => id === b[i]);

export const buildFlatIndex = (
	links: ItemLink[],
	previous: FlatIndex,
	previousChildren: ChildrenMap,
	previousParents: ParentMap,
) => {
	const flatIndex: FlatIndex = {};
	const childrenMap: ChildrenMap = {};
	const parentMap: ParentMap = {};

	const visit = (link: ItemLink) => {
		const id = getTreeId(link);
		flatIndex[id] = sameItem(previous[id], link) ? previous[id] : link;

		const children = (link as CategoryLink).items ?? [];
		const childIds = children.map(getTreeId);
		childrenMap[id] = sameIds(previousChildren[id], childIds) ? previousChildren[id] : childIds;

		children.forEach((child) => {
			parentMap[getTreeId(child)] = id;
			visit(child);
		});
	};

	const rootIds = links.map((link) => {
		visit(link);
		return getTreeId(link);
	});

	const sameMap = <T>(before: Record<string, T>, after: Record<string, T>): boolean => {
		const keys = Object.keys(after);
		return keys.length === Object.keys(before).length && keys.every((key) => before[key] === after[key]);
	};

	return {
		flatIndex: sameMap(previous, flatIndex) ? previous : flatIndex,
		childrenMap: sameMap(previousChildren, childrenMap) ? previousChildren : childrenMap,
		parentMap: sameMap(previousParents, parentMap) ? previousParents : parentMap,
		rootIds,
	};
};

export const reconcileExpansion = (
	nextIndex: FlatIndex,
	previousIndex: FlatIndex,
	previousExpanded: ReadonlySet<string>,
): { expanded: Set<string>; selectedId: string } => {
	const expanded = new Set(previousExpanded);
	let selectedId = "";

	for (const link of Object.values(nextIndex)) {
		const id = getTreeId(link);
		if (!previousIndex[id] && (link as CategoryLink).isExpanded) expanded.add(id);
		if (link.isCurrentLink) selectedId = id;
	}

	for (const id of previousExpanded) if (!nextIndex[id]) expanded.delete(id);

	return { expanded, selectedId };
};

const getAutoExpanded = (selectedId: string, parentMap: ParentMap): Set<string> => {
	const autoExpanded = new Set<string>();
	let parentId = parentMap[selectedId];
	while (parentId) {
		autoExpanded.add(parentId);
		parentId = parentMap[parentId];
	}
	return autoExpanded;
};

const createInitialNavigationTreeState = (links: ItemLink[], scope: string | null) => {
	const { flatIndex, childrenMap, parentMap, rootIds } = buildFlatIndex(links, {}, {}, {});
	const { expanded, selectedId } = reconcileExpansion(flatIndex, {}, new Set<string>());
	for (const id of getAutoExpanded(selectedId, parentMap)) expanded.add(id);

	return {
		scope,
		flatIndex,
		childrenMap,
		parentMap,
		rootIds,
		expanded,
		selectedId,
		hoveredParentId: null,
		hoveredAnchorId: null,
		hoveredNavigationId: null,
		draggingId: null,
		dragTarget: null,
		dragLineIds: EMPTY_DRAG_LINE_IDS,
		isDragLocked: false,
		filteredOutIds: EMPTY_FILTERED_OUT_IDS,
		isGrouped: false,
		catalogBehavior: NO_CATALOG_BEHAVIOR,
		layoutVersion: 0,
		onDrop: null,
		onToggle: null,
		onCreateArticle: null,
	};
};

const createNavigationTreeState =
	(initialLinks: ItemLink[], initialScope: string | null): StateCreator<NavigationTreeStore> =>
	(set, get) => ({
		...createInitialNavigationTreeState(initialLinks, initialScope),

		toggleExpanded: (id, open) =>
			set((state) => {
				state.onToggle?.(id, open);
				const next = new Set(state.expanded);
				if (open) next.add(id);
				else {
					const stack = [id];
					while (stack.length) {
						const current = stack.pop()!;
						next.delete(current);
						for (const child of state.childrenMap[current] ?? []) stack.push(child);
					}
				}
				return { expanded: next };
			}),

		select: (id) =>
			set((state) => {
				state.onToggle?.(id, true);
				const next = new Set(state.expanded);
				next.add(id);
				return { selectedId: id, expanded: next };
			}),

		setHover: (parentId, anchorId) =>
			set((state) => {
				if (state.hoveredParentId === parentId && state.hoveredAnchorId === anchorId) return state;
				return { hoveredParentId: parentId, hoveredAnchorId: anchorId };
			}),

		setHoveredNavigationId: (id) =>
			set((state) => (state.hoveredNavigationId === id ? state : { hoveredNavigationId: id })),

		setDragging: (id) => set({ draggingId: id, dragTarget: null, dragLineIds: EMPTY_DRAG_LINE_IDS }),

		setDragTarget: (target) =>
			set((state) => {
				const allowed = target && isAllowedDrop(state, target) ? target : null;
				return sameDragTarget(state.dragTarget, allowed)
					? state
					: { dragTarget: allowed, dragLineIds: getDragLineIds(allowed, state.childrenMap) };
			}),

		setNavItems: (links, scope) =>
			set((state) => {
				const nextScope = scope ?? state.scope;
				const scopeChanged = nextScope !== state.scope;
				const previousIndex = scopeChanged ? {} : state.flatIndex;
				const previousSelectedId = (() => {
					const current = Object.values(previousIndex).find((item) => item.isCurrentLink);
					return current ? getTreeId(current) : "";
				})();
				const previousChildren = scopeChanged ? {} : state.childrenMap;
				const previousParents = scopeChanged ? {} : state.parentMap;
				const previousExpanded = scopeChanged ? new Set<string>() : state.expanded;
				const previousAutoExpanded = scopeChanged
					? new Set<string>()
					: getAutoExpanded(previousSelectedId, state.parentMap);
				const { flatIndex, childrenMap, parentMap, rootIds } = buildFlatIndex(
					links,
					previousIndex,
					previousChildren,
					previousParents,
				);

				const { expanded, selectedId } = reconcileExpansion(flatIndex, previousIndex, previousExpanded);
				if (scopeChanged || selectedId !== previousSelectedId) {
					const autoExpanded = getAutoExpanded(selectedId, parentMap);

					for (const id of previousAutoExpanded) {
						if (autoExpanded.has(id)) continue;
						const item = flatIndex[id] as CategoryLink | undefined;
						if (item?.isExpanded) expanded.add(id);
						else expanded.delete(id);
					}
					for (const id of autoExpanded) expanded.add(id);
				}

				return {
					scope: nextScope,
					flatIndex,
					childrenMap,
					dragLineIds: scopeChanged ? EMPTY_DRAG_LINE_IDS : getDragLineIds(state.dragTarget, childrenMap),
					parentMap,
					rootIds: !scopeChanged && sameIds(state.rootIds, rootIds) ? state.rootIds : rootIds,
					expanded,
					selectedId,
					...(scopeChanged && {
						filteredOutIds: EMPTY_FILTERED_OUT_IDS,
						hoveredParentId: null,
						hoveredAnchorId: null,
						hoveredNavigationId: null,
						draggingId: null,
						dragTarget: null,
						isDragLocked: false,
					}),
				};
			}),

		moveToGroup: (itemId, groupId) =>
			set((state) => {
				if (!state.flatIndex[groupId] || !state.flatIndex[itemId]) return state;
				if (state.parentMap[itemId] === groupId) return state;

				const previousParentId = state.parentMap[itemId];
				const childrenMap = { ...state.childrenMap };
				if (previousParentId)
					childrenMap[previousParentId] = (childrenMap[previousParentId] ?? []).filter((id) => id !== itemId);

				const path = state.flatIndex[itemId]?.ref?.path;
				const alreadyInGroup = (state.childrenMap[groupId] ?? []).some(
					(id) => id !== itemId && state.flatIndex[id]?.ref?.path === path,
				);

				if (alreadyInGroup) {
					const { [itemId]: _droppedItem, ...flatIndex } = state.flatIndex;
					const { [itemId]: _droppedParent, ...parentMap } = state.parentMap;
					return {
						childrenMap,
						flatIndex,
						parentMap,
						rootIds: state.rootIds.filter((id) => id !== itemId),
					};
				}

				childrenMap[groupId] = [...(childrenMap[groupId] ?? []).filter((id) => id !== itemId), itemId];

				return {
					childrenMap,
					parentMap: { ...state.parentMap, [itemId]: groupId },
					rootIds: previousParentId ? state.rootIds : state.rootIds.filter((id) => id !== itemId),
					expanded: state.expanded.has(groupId) ? state.expanded : new Set(state.expanded).add(groupId),
				};
			}),

		snapshotTree: () => {
			const { flatIndex, childrenMap, parentMap, rootIds, expanded } = get();
			return { flatIndex, childrenMap, parentMap, rootIds, expanded };
		},

		restoreTree: (snapshot) => set(snapshot),

		markFilteredOut: (itemId, filteredOut) =>
			set((state) => {
				if (state.filteredOutIds.has(itemId) === filteredOut) return state;
				const next = new Set(state.filteredOutIds);
				if (filteredOut) next.add(itemId);
				else next.delete(itemId);
				return { filteredOutIds: next };
			}),

		setGrouped: (isGrouped) => set((state) => (state.isGrouped === isGrouped ? state : { isGrouped })),

		setDragLocked: (locked) => set({ isDragLocked: locked }),
		setCatalogBehavior: (behavior) => set({ catalogBehavior: behavior }),
		notifyLayoutSettled: () => set((state) => ({ layoutVersion: state.layoutVersion + 1 })),
		setOnDrop: (fn) => set({ onDrop: fn }),
		setOnToggle: (fn) => set({ onToggle: fn }),
		setOnCreateArticle: (fn) => set({ onCreateArticle: fn }),
	});

export const createNavigationTreeStore = (initialLinks: ItemLink[] = [], scope: string | null = null) =>
	createStore<NavigationTreeStore>()(createNavigationTreeState(initialLinks, scope));

export type NavigationTreeStoreApi = ReturnType<typeof createNavigationTreeStore>;

const NavigationTreeStoreContext = createContext<NavigationTreeStoreApi | null>(null);

export const NavigationTreeStoreProvider = ({
	children,
	initialLinks,
	scope,
}: {
	children?: ReactNode;
	initialLinks: ItemLink[];
	scope: string;
}) => {
	const storeRef = useRef<NavigationTreeStoreApi | null>(null);
	if (storeRef.current === null) storeRef.current = createNavigationTreeStore(initialLinks, scope);

	return createElement(NavigationTreeStoreContext.Provider, { value: storeRef.current }, children);
};

/** Fallback store for consumers outside a scoped provider and imperative test setup. */
export const navigationTreeStore = createNavigationTreeStore();

export const useNavigationTreeStoreApi = (): NavigationTreeStoreApi =>
	useContext(NavigationTreeStoreContext) ?? navigationTreeStore;

export const useNavigationTreeStore = <T>(selector: (state: NavigationTreeStore) => T): T =>
	useStore(useNavigationTreeStoreApi(), useShallow(selector));
